import { SitePage } from '@web-studio/core-components';
import {
  blockParityIdentitiesEqual,
  extractBlockParityIdentities,
  extractMainMarkup,
  extractWsSiteRootInnerHtml,
  normalizeParityHtml,
  renderSiteHtml,
  stripStudioEditChrome,
} from '@web-studio/core-components/html';
import type { LayoutBlock } from '@web-studio/shared-schemas';
import { createElement } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';

export interface ParityReport {
  reactLength: number;
  ssrLength: number;
  /** Normalized full HTML equality (aspirational production markup). */
  matches: boolean;
  /** Same ordered data-block-id + data-component markers (CI-level bar). */
  structuralMatch: boolean;
  reactExcerpt: string;
  ssrExcerpt: string;
}

/** Inner HTML of `#ws-site-root` from a static React `SitePage` render (editable: false). */
export function renderReactSiteInnerHtml(
  blocks: LayoutBlock[],
  locale: string,
  themeId: string,
  colorsetId: string,
): string {
  const markup = renderToStaticMarkup(
    createElement(SitePage, {
      blocks,
      locale,
      themeId,
      colorsetId,
      editable: false,
    }),
  );
  return extractWsSiteRootInnerHtml(markup);
}

export function buildParityReport(
  blocks: LayoutBlock[],
  locale: string,
  themeId: string,
  colorsetId: string,
  reactInnerHtml: string,
): ParityReport {
  const full = renderSiteHtml({
    blocks,
    locale,
    themeId,
    colorsetId,
    title: 'Design Lab Parity',
  });
  const ssrMain = extractMainMarkup(full);
  const reactTrim = stripStudioEditChrome(reactInnerHtml).trim();
  const normReact = normalizeParityHtml(reactTrim);
  const normSsr = normalizeParityHtml(ssrMain);
  const structuralMatch = blockParityIdentitiesEqual(
    extractBlockParityIdentities(reactTrim),
    extractBlockParityIdentities(ssrMain),
  );

  return {
    reactLength: reactTrim.length,
    ssrLength: ssrMain.length,
    matches: normReact.length > 0 && normReact === normSsr,
    structuralMatch: reactTrim.length > 0 && structuralMatch,
    reactExcerpt: reactTrim,
    ssrExcerpt: ssrMain,
  };
}

/** Batch / live verdict: static React markup vs `renderSiteHtml` (ignores editable canvas chrome). */
export function buildStaticParityReport(
  blocks: LayoutBlock[],
  locale: string,
  themeId: string,
  colorsetId: string,
): ParityReport {
  return buildParityReport(
    blocks,
    locale,
    themeId,
    colorsetId,
    renderReactSiteInnerHtml(blocks, locale, themeId, colorsetId),
  );
}
